import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Trophy, Gift, Star, Users, CheckCircle, Clock, 
  ExternalLink, MessageCircle, Facebook, AlertCircle 
} from 'lucide-react';

const WHATSAPP_GROUP = "https://chat.whatsapp.com/FASs6nHFVdd60zp6w4XkM1?mode=gi_t";
const FACEBOOK_GROUP = "https://www.facebook.com/share/g/1BqJUUvUDu/";

export default function FreeRafflePage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        api.get('/free-raffle/settings'),
        user ? api.get('/free-raffle/my-status') : Promise.resolve({ data: null })
      ]);
      setSettings(settingsRes.data);
      setMyStatus(statusRes.data);
    } catch (error) {
      console.error('Error fetching free raffle data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para postularte');
      return;
    }

    setApplying(true);
    try {
      const response = await api.post('/free-raffle/apply');
      toast.success(response.data.message);
      setMyStatus({ status: 'pending' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al postularse');
    } finally {
      setApplying(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isEnabled = settings?.enabled;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-slate-100 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
        <div className="max-w-lg mx-auto text-center">
          <Gift className="w-16 h-16 mx-auto mb-4 animate-bounce" />
          <h1 className="text-2xl font-bold mb-2">Sorteo Gratuito</h1>
          <p className="text-purple-100">
            {isEnabled 
              ? "¡Participa gratis siguiendo nuestras redes!" 
              : "El sorteo gratuito no está activo actualmente"}
          </p>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-6">
        {/* Status Banner */}
        {!isEnabled && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <p className="text-amber-800 text-sm">
                El sorteo gratuito está temporalmente inactivo. Síguenos en redes para enterarte cuando vuelva a activarse.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Prizes Section */}
        {isEnabled && (
          <Card className="border-2 border-purple-200 bg-white">
            <CardHeader className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-6 h-6" />
                Premios del Sorteo
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🏆</span>
                  <span className="font-medium">Premio Mayor</span>
                </div>
                <span className="font-bold text-lg text-yellow-700">
                  {formatCurrency(settings?.prize_major || 500000)}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔁</span>
                  <span className="font-medium">Número Invertido</span>
                </div>
                <span className="font-bold text-lg text-purple-700">
                  {formatCurrency(settings?.prize_inverted || 300000)}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔢</span>
                  <span className="font-medium">Últimas 2 cifras</span>
                </div>
                <span className="font-bold text-lg text-blue-700">
                  {formatCurrency(settings?.prize_last_two || 150000)}
                </span>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🔢</span>
                  <span className="font-medium">Primeras 2 cifras</span>
                </div>
                <span className="font-bold text-lg text-emerald-700">
                  {formatCurrency(settings?.prize_first_two || 50000)}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* My Status */}
        {user && myStatus && myStatus.status !== 'not_applied' && (
          <Card className={`border-2 ${
            myStatus.status === 'validated' ? 'border-green-300 bg-green-50' :
            myStatus.status === 'pending' ? 'border-amber-300 bg-amber-50' :
            'border-red-300 bg-red-50'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {myStatus.status === 'validated' ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : myStatus.status === 'pending' ? (
                  <Clock className="w-8 h-8 text-amber-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-red-600" />
                )}
                <div>
                  <h3 className="font-bold">
                    {myStatus.status === 'validated' ? '¡Estás participando!' :
                     myStatus.status === 'pending' ? 'Postulación pendiente' :
                     'Postulación rechazada'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {myStatus.status === 'validated' 
                      ? `Tus números: ${myStatus.ticket_numbers?.join(', ') || 'Por asignar'}`
                      : myStatus.status === 'pending'
                      ? 'Envía tu comprobante al grupo de WhatsApp'
                      : 'Contacta a soporte para más información'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Apply Button */}
        {isEnabled && user && (!myStatus || myStatus.status === 'not_applied') && (
          <Button 
            onClick={handleApply}
            disabled={applying}
            className="w-full h-14 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="apply-free-raffle-btn"
          >
            {applying ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            ) : (
              <>
                <Star className="w-6 h-6 mr-2" />
                Postularme al Sorteo Gratuito
              </>
            )}
          </Button>
        )}

        {/* Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-primary" />
              Condiciones de Participación
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-xl">📱</span>
              <div>
                <p className="font-medium">Seguir redes sociales oficiales</p>
                <p className="text-sm text-muted-foreground">Instagram, TikTok y Facebook de TuPrima</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-xl">🎮</span>
              <div>
                <p className="font-medium">Participar en dinámicas diarias</p>
                <p className="text-sm text-muted-foreground">Completa las actividades publicadas</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-xl">👥</span>
              <div>
                <p className="font-medium">Estar en grupos oficiales</p>
                <p className="text-sm text-muted-foreground">WhatsApp y Facebook de TuPrima</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <span className="text-xl">✅</span>
              <div>
                <p className="font-medium">Cumplir instrucciones del sorteo</p>
                <p className="text-sm text-muted-foreground">El incumplimiento invalida la participación</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Validation Info */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Validación vía WhatsApp
            </h3>
            <p className="text-sm text-blue-700 mb-3">
              Los comprobantes de seguimiento se envían al grupo oficial de WhatsApp. 
              La validación es manual y una vez aprobado, se te asignarán los números.
            </p>
          </CardContent>
        </Card>

        {/* Social Links */}
        <div className="space-y-3">
          <h3 className="font-bold text-center">Únete a nuestros grupos</h3>
          
          <a
            href={WHATSAPP_GROUP}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full p-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
          >
            <MessageCircle className="w-6 h-6" />
            Grupo de WhatsApp
            <ExternalLink className="w-4 h-4" />
          </a>
          
          <a
            href={FACEBOOK_GROUP}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 w-full p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
          >
            <Facebook className="w-6 h-6" />
            Grupo de Facebook
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Participants Count */}
        {isEnabled && (
          <div className="text-center text-sm text-muted-foreground">
            <Users className="w-4 h-4 inline mr-1" />
            {settings?.participants_count || 0} participantes validados
            {settings?.pending_count > 0 && ` • ${settings.pending_count} pendientes`}
          </div>
        )}
      </div>
    </div>
  );
}
